<template>
    <BookingFormLayout
        v-bind="
            bookingFormSharedData
                .setContainerClasses('max-w-3xl')
                .setCoverImageHidden()
                .getBookingFormLayoutProps()
        "
    >
        <template #content>
            <div class="max-w-xl mx-auto py-8">
                <div class="space-y-16">
                    <div class="text-center space-y-2">
                        <div
                            class="flex flex-col lg:flex-row items-center justify-center h-full gap-2"
                        >
                            <Icon
                                name="circle-check"
                                class="w-8 h-8 text-active"
                            />
                            <span class="text-2xl font-medium text-center">
                                {{
                                    localizer.translate(
                                        TextTokenEnum.BookingForm_MeetingSuccessfullyBooked,
                                    )
                                }}!
                            </span>
                        </div>
                        <span class="text-gray-500 text-center">
                            {{
                                localizer.translate(
                                    TextTokenEnum.BookingForm_YouCanFindTheDetailsBelow,
                                )
                            }}.
                        </span>
                    </div>
                    <MeetingSelectedDetails
                        :company="localCompany"
                        :service="localFirstMeetingData.service"
                        :price="
                            localFirstReservationData.bookingPayment?.amount
                        "
                        :numberOfReservedSeats="
                            localFirstReservationData.reservation
                                .numberOfSeatsReserved
                        "
                        :user="localFirstMeetingData.user"
                        :enabledLocationTypes="
                            localFirstMeetingData.meeting.locationType
                                ? [localFirstMeetingData.meeting.locationType]
                                : null
                        "
                        :selectedTimeZone="
                            localFirstReservationData.reservation
                                .selectedTimeZone
                        "
                        :meetingUniqueCode="
                            localFirstMeetingData.meeting.uniqueCode
                        "
                        :meetingLocationIconSize="16"
                        :meetingDates="
                            localScheduledRecurringDatesInSelectedTimezone
                        "
                        :recurrenceSettings="null"
                        :forceShowAllDates="true"
                        :localizer="localizer"
                    />
                    <div class="text-sm text-gray-600">
                        {{
                            localizer.translate(
                                TextTokenEnum.BookingForm_NeedToRescheduleOrCancelThisMeeting,
                            )
                        }}?
                        <a
                            class="hover:underline text-blue-500 visited:text-purple-500"
                            :href="
                                urlFactory.makeGuestMeetingViewPageUrl(
                                    localFirstMeetingData.meeting.bookingId,
                                    localFirstReservationData.reservation
                                        .companyCustomerId,
                                )
                            "
                        >
                            {{
                                localizer.translate(
                                    TextTokenEnum.BookingForm_ClickHere,
                                )
                            }}.
                        </a>
                    </div>
                </div>
            </div>
        </template>
    </BookingFormLayout>
</template>

<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import Icon from '@/Shared/Component/Icon.vue'
    import BookingFormLayout from '@/Shared/Layout/BookingForm/BookingFormLayout.vue'
    import MeetingSelectedDetails from '@/BookingForm/BookingForm/Page/Step/MeetingSelectedDetails.vue'
    import BookingFormSharedData from '@/Shared/Entity/BookingFormSharedData'
    import RecurringDate from '@/App/Booking/Recurring/Entity/RecurringDate'
    import LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'
    import Company from '@/App/Company/Entity/Company'
    import MeetingData from '@/App/Booking/Meeting/Entity/MeetingData'

    interface ShowBookingFormSuccessStepProps {
        bookingFormSharedDataData: BookingFormSharedData
        companyData: any
        firstMeetingDataData: any
        scheduledRecurringDatesInSelectedTimezoneData: any[]
    }

    const props = defineProps<ShowBookingFormSuccessStepProps>()
    const bookingFormSharedData = BookingFormSharedData.hydrate(
        props.bookingFormSharedDataData,
    )
    const localCompany = Company.hydrateSingle(props.companyData)
    const localFirstMeetingData = MeetingData.hydrateSingle(
        props.firstMeetingDataData,
    )
    const localFirstReservationData =
        localFirstMeetingData.getSingleExistingReservationData()
    const localScheduledRecurringDatesInSelectedTimezone =
        RecurringDate.hydrateMultiple(
            props.scheduledRecurringDatesInSelectedTimezoneData,
        )
    const localizer = LocalizerService.make(bookingFormSharedData.currentLocale)
    const urlFactory = new UrlFactory()
</script>
